import {
  Component,
  DestroyRef,
  Injectable,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/internal/operators/tap';
import { delay } from 'rxjs/operators';
import { LoadingCloseReason } from '../../shared.types';

interface LoadingOptions {
  message: string;
  observableToFinish$: Observable<any>;
  cancelButton?: boolean;
}

@Injectable({ providedIn: 'root' })
export class LoadingService {
  private ngbModal = inject(NgbModal);

  public wait(
    options: LoadingOptions
  ): Observable<{ closeReason: LoadingCloseReason; observablePayload: any }> {
    const modalRef = this.ngbModal.open(LoadingDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'modal-size-sm',
    });
    modalRef.componentInstance.options = options;

    return modalRef.closed;
  }
}

@Component({
  selector: 'app-loading-dialog',
  styleUrls: ['loading-dialog.component.scss'],
  templateUrl: 'loading-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslateModule],
})
export class LoadingDialogComponent implements OnInit {
  private modalRef = inject(NgbActiveModal);
  private destroyRef = inject(DestroyRef);

  public options: LoadingOptions;

  protected showCancelButton(): boolean {
    return this.options.cancelButton ?? true;
  }

  protected cancel() {
    this.modalRef.close({
      closeReason: LoadingCloseReason.Cancel,
    });
  }

  ngOnInit(): void {
    let payload = null;
    let hasError = false;

    this.options.observableToFinish$
      .pipe(
        delay(500),
        takeUntilDestroyed(this.destroyRef),
        tap(
          (load: any) => {
            payload = load;
          },
          (err) => {
            hasError = true;
          }
        )
      )
      .subscribe(
        () => {
          this.modalRef.close({
            closeReason: LoadingCloseReason.LoadingFinished,
            observablePayload: payload,
          });
        },
        () => {
          this.modalRef.close({
            closeReason: LoadingCloseReason.Error,
          });
        }
      );
  }
}
